import { AnyDoc } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";

export type RecentlyViewedDoc = {
  doc: AnyDoc;
  viewedAt: Date;
};

export const useRecentlyViewedDocs = (limit: number) => {
  const recentQuery = useClientManager("docs")
    .getRecentlyViewed()
    .useQuery({ limit }, { keepPreviousData: true });

  const entries = recentQuery.data ?? [];
  const docIds = entries.map((entry) => entry.docId);

  const docsQuery = useClientManager("docs")
    .retrieveDocs()
    .useQuery({ docIds }, { enabled: entries.length > 0, keepPreviousData: true });

  const docsById = new Map<string, AnyDoc>(
    (docsQuery.data ?? []).filter((doc): doc is AnyDoc => doc !== null).map((doc) => [doc.docId, doc]),
  );

  const items: RecentlyViewedDoc[] = entries.flatMap((entry) => {
    const doc = docsById.get(entry.docId);
    if (!doc || doc.isArchived) return [];
    return [{ doc, viewedAt: entry.viewedAt }];
  });

  const isResolving =
    (recentQuery.isLoading && recentQuery.data === undefined) ||
    (entries.length > 0 && docsQuery.isLoading && docsQuery.data === undefined);
  const error = recentQuery.error ?? docsQuery.error;

  const refetch = async () => {
    await recentQuery.refetch();
    await docsQuery.refetch();
  };

  return {
    data: isResolving ? undefined : items,
    error: error ?? null,
    isResolving,
    refetch,
  };
};
